import { useEffect, useState } from "react";
import { Send, Phone, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";
import { useInView, useAfterLoad } from "@/hooks/use-in-view";

interface FormEmbedProps {
  id?: string;
  className?: string;
}

/**
 * Estimate request form.
 *
 * When `siteConfig.widgets.formIframeSrc` is set, the LeadConnector form is
 * embedded exactly as provided by the widget code — we never force a height,
 * scroll or compression, since form_embed.js controls the iframe height.
 *
 * The iframe and its resize script are held back until the form is close to
 * the viewport. On a desktop hero the form is on screen at load, so it mounts
 * immediately exactly as before; on a phone, where it sits well below the
 * fold, the browser no longer spends its first seconds of main-thread time on
 * a third-party form nobody has scrolled to yet. A placeholder of the same
 * height holds the space so nothing shifts when it arrives.
 *
 * If the widget ID is ever cleared, this falls back to a built-in form that
 * composes a structured email to the business inbox.
 */
const FormEmbed = ({ id = "ft-estimate-form", className = "" }: FormEmbedProps) => {
  const embedSrc = siteConfig.widgets.formIframeSrc;
  const { ref, inView } = useInView<HTMLDivElement>("400px");
  // Two gates: near the viewport, and the page has finished loading. The
  // second one matters on the desktop hero, where the form is on screen from
  // the start and would otherwise fetch ~580kB of third-party iframe while
  // the page is still painting.
  const afterLoad = useAfterLoad();
  const show = inView && afterLoad;
  // form_embed.js sizes the iframe from its inner document, and reports a
  // near-zero height while that document is still empty — which would collapse
  // the panel for a beat. Hold the reserved height until the frame reports
  // loaded, then hand sizing back to the script as before.
  const [frameLoaded, setFrameLoaded] = useState(false);

  useEffect(() => {
    if (!embedSrc || !show) return;
    const scriptId = "lc-form-embed-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.formScriptSrc;
      s.async = true;
      document.body.appendChild(s);
    }
  }, [embedSrc, show]);

  if (embedSrc) {
    // LeadConnector keys its resize messages off the iframe id, so the id must
    // match data-layout-iframe-id and stay unique per mounted instance.
    const frameId = siteConfig.widgets.formId ? `inline-${siteConfig.widgets.formId}-${id}` : id;
    return (
      <div
        ref={ref}
        className={className}
        style={frameLoaded ? undefined : { minHeight: siteConfig.widgets.formHeight }}
      >
        {show ? (
          <iframe
            src={embedSrc}
            onLoad={() => setFrameLoaded(true)}
            style={{
              width: "100%",
              height: siteConfig.widgets.formHeight,
              border: "none",
              borderRadius: 10,
              display: "block",
            }}
            id={frameId}
            data-layout="{'id':'INLINE'}"
            data-trigger-type="alwaysShow"
            data-trigger-value=""
            data-activation-type="alwaysActivated"
            data-activation-value=""
            data-deactivation-type="neverDeactivate"
            data-deactivation-value=""
            data-form-name={siteConfig.widgets.formName}
            data-height={siteConfig.widgets.formHeight}
            data-layout-iframe-id={frameId}
            data-form-id={siteConfig.widgets.formId ?? undefined}
            title={siteConfig.widgets.formName}
            loading="lazy"
          />
        ) : null}
      </div>
    );
  }

  return (
    <div ref={ref} className={className}>
      <NativeEstimateForm formId={id} />
    </div>
  );
};

const inputClass =
  "w-full rounded-lg border border-border bg-background px-3.5 py-2.5 text-sm text-foreground " +
  "placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent";

const NativeEstimateForm = ({ formId }: { formId: string }) => {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    city: "",
    service: "",
    details: "",
  });

  const set = (k: keyof typeof form) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `Estimate request — ${form.service || "General"} — ${form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Phone: ${form.phone}`,
      `Email: ${form.email}`,
      `City: ${form.city}`,
      `Service: ${form.service}`,
      "",
      "Project details:",
      form.details,
    ].join("\n");
    window.location.href = `${siteConfig.emailHref}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  if (sent) {
    return (
      <div className="text-center py-8 px-2">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
          <CheckCircle2 className="h-7 w-7 text-primary" />
        </div>
        <h4 className="font-heading text-xl font-bold text-foreground mb-2">Almost there</h4>
        <p className="text-muted-foreground text-sm mb-5 max-w-sm mx-auto">
          Your email app should have opened with the details filled in — hit send and we'll get
          back to you. Prefer to talk it through instead?
        </p>
        <a href={siteConfig.phoneHref} className="inline-flex w-fit">
          <Button className="gap-2 rounded-xl font-semibold">
            <Phone className="h-4 w-4" /> {siteConfig.phone}
          </Button>
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} id={formId} className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${formId}-name`} className="sr-only">Full name</label>
          <input
            id={`${formId}-name`} name="name" required value={form.name} onChange={set("name")}
            className={inputClass} placeholder="Full name *" autoComplete="name"
          />
        </div>
        <div>
          <label htmlFor={`${formId}-phone`} className="sr-only">Phone number</label>
          <input
            id={`${formId}-phone`} name="phone" type="tel" required value={form.phone} onChange={set("phone")}
            className={inputClass} placeholder="Phone *" autoComplete="tel"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${formId}-email`} className="sr-only">Email address</label>
          <input
            id={`${formId}-email`} name="email" type="email" value={form.email} onChange={set("email")}
            className={inputClass} placeholder="Email" autoComplete="email"
          />
        </div>
        <div>
          <label htmlFor={`${formId}-city`} className="sr-only">City</label>
          <input
            id={`${formId}-city`} name="city" value={form.city} onChange={set("city")}
            className={inputClass} placeholder="City" autoComplete="address-level2"
          />
        </div>
      </div>

      <div>
        <label htmlFor={`${formId}-service`} className="sr-only">Service needed</label>
        <select
          id={`${formId}-service`} name="service" required value={form.service} onChange={set("service")}
          className={`${inputClass} ${form.service ? "" : "text-muted-foreground"}`}
        >
          <option value="">What do you need? *</option>
          {services.map((s) => (
            <option key={s.slug} value={s.title}>{s.title}</option>
          ))}
          <option value="Multiple / Full Remodel">Multiple / Full Remodel</option>
          <option value="Something Else">Something Else</option>
        </select>
      </div>

      <div>
        <label htmlFor={`${formId}-details`} className="sr-only">Project details</label>
        <textarea
          id={`${formId}-details`} name="details" rows={4} value={form.details} onChange={set("details")}
          className={inputClass} placeholder="Tell us about the project — rooms, size, timeline, anything helpful."
        />
      </div>

      <Button type="submit" size="lg" className="w-full gap-2 rounded-xl font-semibold">
        Request My Free Estimate <Send className="h-4 w-4" />
      </Button>

      <p className="text-muted-foreground text-xs text-center pt-1">
        Or call / text{" "}
        <a href={siteConfig.phoneHref} className="text-primary font-semibold hover:underline">
          {siteConfig.phone}
        </a>{" "}
        · {siteConfig.hours.summary}
      </p>
    </form>
  );
};

export default FormEmbed;
